import { ChainedCommands, Editor } from "@tiptap/core";

/**
 * A handler to insert katex expression into out editor.
 *
 * This function has a sanity check to make sure any expression inserted like
 * `$$\frac{1}{2}$$` or just simply
 * `\frac{1}{2}`
 * can still be inserted just fine.
 *
 * This function handles empty `expression` inserted as well
 *
 * This function also handles the cursor position too so that the math expression will always be rendered after the
 * inserted expression
 */
export const insertMathExpression = ({
    editor,
    pos,
    expression,
    chain,
}: { editor: Editor; chain: () => ChainedCommands; pos: { start: number; end: number }; expression: string }) => {
    // if expression is empty means user want's to delete the math expression
    if (!expression.trim()) {
        chain().insertContentAt({ from: pos.start, to: pos.end }, "").setTextSelection(pos.start).run();
        return;
    }

    const docSize = editor.state.doc.content.size;
    const nextChar = editor.state.doc.textBetween(pos.end, Math.min(pos.end + 1, docSize));
    const shouldSuffixSpace = nextChar !== " ";

    const newExpression = (() => {
        const isStartWith$$ = expression.startsWith("$$");
        const isEndsWith$$ = expression.endsWith("$$");

        if (isStartWith$$ && isEndsWith$$) {
            return expression;
        } else if (!isStartWith$$ && !isEndsWith$$) {
            return `$$${expression}$$`;
        }
        throw new Error(`Math Invalid expression: ${expression}`);
    })();

    const value = shouldSuffixSpace ? `${newExpression} ` : newExpression;

    const newCursorPos = pos.start + value.length + 1;
    chain()
        .focus()
        // insert the content
        .insertContentAt({ from: pos.start, to: pos.end }, value)
        // and we must always move the cursor to the end to make sure the math expression is rendered properly
        .setTextSelection(newCursorPos)
        .run();
};
